import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Customer, Option, Order } from './types';
import { axios } from '../api';
import { Spinner } from '../assets/Spinner';
import { CheckboxTable } from '../component/CheckboxTable';
import { ConfirmationModal } from '../component/ConfirmationModal';
import { NotFound } from '../component/NotFound';
import { SearchableDropdown } from '../component/SearchableDropdown';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { QuickSelectRefundButton } from '../component/buttons/QuickSelectRefundButton';
import { useCustomParams } from '../hooks/useCustomParams';
import { CustomerRefundsHeader, ShippingRefundsHeader } from '../tableHeaders';
import {
  BOTTOM_TOAST,
  calculatePriceAfterDiscount,
  allocateRefundTaxAcrossRows,
  calculateExactCloseoutTaxAmount,
  calculateAmountRefundedWithoutTax,
  calculateHalfRefundRoundedUp,
  calculateUnitPriceAfterDiscount,
  generateErrorMsg,
  generateFullName,
  getCountryFlag,
  getOrder,
  parseCurrency,
  SHIPMENT_DISPLAY_VALUE,
} from '../utils';

export const CustomerRefund = () => {
  const navigate = useNavigate();
  const { customerId, orderId } = useCustomParams();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [isShippingRefundSelected, setIsShippingRefundSelected] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [refundAmounts, setRefundAmounts] = useState<string[]>([]);
  const [shippingRefundAmount, setShippingRefundAmount] = useState<string>('0.00');
  const [isProcessLoading, setIsProcessLoading] = useState(false);
  const [refundReasonOptions, setRefundReasonOptions] = useState<Option[]>([]);
  const [refundReason, setRefundReason] = useState<Option | null>(null);

  useEffect(() => {
    getCustomer();
    axios
      .get('refunds/reasons')
      .then((response) => {
        setRefundReasonOptions(response.data);
      })
      .catch((error) => {
        toast.error(generateErrorMsg(error), BOTTOM_TOAST);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const order = useMemo(() => {
    return getOrder(customer?.orders, orderId);
  }, [customer?.orders, orderId]);

  const shippingData = useMemo(() => {
    if (!order) return {};
    const subtotalShippingPrice =
      order.shipping_subtotal_price ??
      (parseCurrency(order.total_shipping_price) - parseCurrency(order.shipping_tax)).toFixed(2);
    const shippingTax = order.shipping_tax ?? '0.00';
    const shippingMethod = order.shipping_method ?? 'Standard Shipping';
    const alreadyRefundedShipping = calculateAmountRefundedWithoutTax(
      subtotalShippingPrice,
      order.total_shipping_refund ?? '0.00',
      shippingTax
    );
    const availableForRefund = (
      parseCurrency(subtotalShippingPrice) - parseCurrency(alreadyRefundedShipping)
    ).toFixed(2);
    const totalRefundIncludingTax = (
      parseCurrency(subtotalShippingPrice) + parseCurrency(shippingTax)
    ).toFixed(2);
    setShippingRefundAmount(totalRefundIncludingTax);

    return {
      Shipping: SHIPMENT_DISPLAY_VALUE[shippingMethod] ?? shippingMethod,
      'Price Paid': `$${subtotalShippingPrice}`, // Tax excl
      'Already Refunded': `$${alreadyRefundedShipping}`, // Tax excl
      'Available for Refund': `$${availableForRefund}`, // Tax excl
      'Amount to Refund': (
        <TextInput
          placeholder={isShippingRefundSelected ? `$${availableForRefund}` : '$0.00'}
          disabled
        />
      ), // Tax excl
      'Total Refund Including Tax': isShippingRefundSelected
        ? `$${totalRefundIncludingTax}`
        : '$0.00', // Tax incl
    };
  }, [order, isShippingRefundSelected]);

  const productRefundInfo = useMemo<
    { priceAfterDiscount: string; alreadyRefundedAmount: string; availableForRefund: string }[]
  >(() => {
    if (!order) return [];
    return order.products.map((product: any) => {
      const priceAfterDiscount = calculatePriceAfterDiscount(product);
      const alreadyRefundedAmount = calculateAmountRefundedWithoutTax(
        priceAfterDiscount,
        product.refund_amount ?? '0.00',
        product.tax_amount
      );
      const availableForRefund = (
        Number(priceAfterDiscount) - Number(alreadyRefundedAmount)
      ).toFixed(2);
      return { priceAfterDiscount, alreadyRefundedAmount, availableForRefund };
    });
  }, [order]);

  const refundableIndexes = useMemo(
    () =>
      productRefundInfo.reduce<number[]>((indexes, info, i) => {
        if (Number(info.availableForRefund) > 0) indexes.push(i);
        return indexes;
      }, []),
    [productRefundInfo]
  );

  // Single source of truth for each row's actual tax_amount, shared by the
  // display totals and the submission payload so they can never disagree.
  const refundTaxAmountByIndex = useMemo(() => {
    if (!order) return new Map<number, string>();
    const indexesToSubmit = selectedRows.filter((index) => Number(refundAmounts[index]) > 0);

    const closeoutIndexes = indexesToSubmit.filter(
      (index) =>
        Number(refundAmounts[index]) === Number(productRefundInfo[index].availableForRefund)
    );
    const rateBasedIndexes = indexesToSubmit.filter((index) => !closeoutIndexes.includes(index));

    const rateBasedTaxAmounts = allocateRefundTaxAcrossRows(
      rateBasedIndexes.map((index) => ({
        pricePaid: calculatePriceAfterDiscount(order.products[index]),
        amountToRefund: refundAmounts[index],
        product: order.products[index],
      }))
    );

    const taxAmountByIndex = new Map<number, string>();
    rateBasedIndexes.forEach((index, i) => taxAmountByIndex.set(index, rateBasedTaxAmounts[i]));
    closeoutIndexes.forEach((index) => {
      const orderProduct = order.products[index];
      taxAmountByIndex.set(
        index,
        calculateExactCloseoutTaxAmount(
          calculatePriceAfterDiscount(orderProduct),
          orderProduct.tax_amount,
          orderProduct.refund_amount ?? '0.00',
          refundAmounts[index]
        )
      );
    });
    return taxAmountByIndex;
  }, [order, selectedRows, refundAmounts, productRefundInfo]);

  const totalRefund = useMemo(() => {
    if (!order || order.products.length === 0) {
      return '0.00';
    }
    const productRefundAmount = selectedRows.reduce((total, index) => {
      if (Number(refundAmounts[index]) <= 0) return total;
      const taxAmount = Number(refundTaxAmountByIndex.get(index) ?? '0.00');
      return total + Number(refundAmounts[index]) + taxAmount;
    }, 0);

    const shippingRefundTotal = isShippingRefundSelected ? Number(shippingRefundAmount) : 0;
    return (productRefundAmount + shippingRefundTotal).toFixed(2);
  }, [
    isShippingRefundSelected,
    order,
    refundAmounts,
    refundTaxAmountByIndex,
    selectedRows,
    shippingRefundAmount,
  ]);

  const isRefund100Disabled = refundableIndexes.length === 0;
  const isRefund50Disabled = productRefundInfo.some(
    (info) => Number(info.alreadyRefundedAmount) > 0
  );

  const toCents = (value: string) => Math.round(Number(value) * 100);

  const activeQuickSelect = useMemo(() => {
    if (refundableIndexes.length === 0) return null;
    const allRefundableSelected =
      selectedRows.length === refundableIndexes.length &&
      refundableIndexes.every((i) => selectedRows.includes(i));
    if (!allRefundableSelected) return null;

    const matchesFull = refundableIndexes.every(
      (i) => toCents(refundAmounts[i]) === toCents(productRefundInfo[i].availableForRefund)
    );
    if (matchesFull) return 'full';

    const matchesHalf = refundableIndexes.every(
      (i) =>
        toCents(refundAmounts[i]) ===
        toCents(calculateHalfRefundRoundedUp(productRefundInfo[i].availableForRefund))
    );
    if (matchesHalf) return 'half';

    return null;
  }, [refundableIndexes, selectedRows, refundAmounts, productRefundInfo]);

  const applyQuickSelect = (mode: 'full' | 'half') => {
    if (activeQuickSelect === mode) {
      const newRefundAmounts = [...refundAmounts];
      refundableIndexes.forEach((i) => {
        newRefundAmounts[i] = '';
      });
      setRefundAmounts(newRefundAmounts);
      setSelectedRows([]);
      return;
    }

    const newRefundAmounts = [...refundAmounts];
    refundableIndexes.forEach((i) => {
      const availableForRefund = productRefundInfo[i].availableForRefund;
      newRefundAmounts[i] =
        mode === 'full' ? availableForRefund : calculateHalfRefundRoundedUp(availableForRefund);
    });
    setRefundAmounts(newRefundAmounts);
    setSelectedRows(refundableIndexes);
  };

  const handleCheckboxChange = (index: number) => {
    if (selectedRows.includes(index)) {
      setSelectedRows((prevSelected) => prevSelected.filter((rowIndex) => rowIndex !== index));
      setRefundAmounts((prevAmounts) => {
        const newRefundAmounts = [...prevAmounts];
        newRefundAmounts[index] = '';
        return newRefundAmounts;
      });
    } else {
      setSelectedRows((prevSelected) => [...prevSelected, index]);
    }
  };

  const getCustomer = () => {
    axios
      .get(`accounts/${customerId}`)
      .then((response) => {
        setCustomer(response.data);
        const order = getOrder(response.data.orders, orderId);
        const unit_prices = order.products.map(() => '');
        setRefundAmounts(unit_prices);
      })
      .catch(() => <NotFound />);
  };

  const isRefundDisabled = (index: number, availableForRefund: string) => {
    if (Number(availableForRefund) === 0) {
      return true;
    }
    return selectedRows.indexOf(index) === -1;
  };

  const mapOrderProducts = (order: Order) => {
    return order.products.map((product: any, i: number) => {
      const unitPricePaidAfterDiscount = calculateUnitPriceAfterDiscount(product);
      const { alreadyRefundedAmount, availableForRefund } = productRefundInfo[i];
      return {
        'Product Code': product.code,
        'Product Description': product.description_bark ?? '--',
        'Pet Name': product.pet_plan?.pet_name,
        'Price Paid (after discount)': `${product.quantity} x $${unitPricePaidAfterDiscount}`,
        'Already Refunded': `$${alreadyRefundedAmount}`,
        'Available for Refund': `$${availableForRefund}`,
        'Amount to Refund': (
          <TextInput
            placeholder="$0.00"
            disabled={isRefundDisabled(i, availableForRefund)}
            value={refundAmounts[i]}
            setTextChange={(value) => {
              const newRefundAmounts = [...refundAmounts];
              newRefundAmounts[i] =
                Number(value) > Number(availableForRefund) ? availableForRefund : value;
              setRefundAmounts(newRefundAmounts);
            }}
          />
        ),
        'Total Refund Including Tax': `$${(
          Number(refundAmounts[i] || '0.00') + Number(refundTaxAmountByIndex.get(i) ?? '0.00')
        ).toFixed(2)}`,
      };
    });
  };

  const handleProcessRefund = () => {
    setIsProcessLoading(true);
    const refundableIndexesToSubmit = selectedRows.filter(
      (index) => Number(refundAmounts[index]) > 0
    );

    const orderProductsPayload = refundableIndexesToSubmit.map((index) => {
      const orderProduct = order.products[index];
      return {
        order_product_id: orderProduct.id,
        amount: refundAmounts[index],
        tax_amount: refundTaxAmountByIndex.get(index),
      };
    });

    axios
      .post(`refunds`, {
        account: customerId,
        order_id: orderId,
        order_product_refunds: orderProductsPayload,
        shipping_refunded: isShippingRefundSelected,
        reason: refundReason?.value,
      })
      .then(() => {
        toast.success(`Refund of $${totalRefund} has been processed on this order`, BOTTOM_TOAST);
        navigate(`/customer-lookup/${customerId}/order/${orderId}`);
      })
      .catch((e) => {
        const errMsg = e.response?.data?.message ?? 'Error processing refund';
        toast.error(errMsg, BOTTOM_TOAST);
      })
      .finally(() => {
        setIsProcessLoading(false);
        setShowModal(false);
        setRefundReason(null);
      });
  };

  if (!customer) return <Spinner />;

  return (
    <div className="h-[calc(100vh)] bg-slate50 p-8">
      <div className="pt-3 pb-3">
        <h2 className="mb-1 flex items-center">Refund Order</h2>
        <b>Customer:</b>{' '}
        {`${getCountryFlag(customer.default_country)} ${generateFullName(
          customer.first_name,
          customer.last_name
        )} (${customer.email})`}{' '}
        <br />
        <b>Order:</b> {order.id}
      </div>
      <b className="inline-block pb-2">Refundable Products</b>
      <div className="flex gap-3 pb-3">
        <QuickSelectRefundButton
          label={
            <>
              Refund <b>100%</b> of products
            </>
          }
          checked={activeQuickSelect === 'full'}
          isDisabled={isRefund100Disabled}
          onClick={() => applyQuickSelect('full')}
        />
        <QuickSelectRefundButton
          label={
            <>
              Refund <b>50%</b> of products
            </>
          }
          checked={activeQuickSelect === 'half'}
          isDisabled={isRefund50Disabled}
          onClick={() => applyQuickSelect('half')}
        />
      </div>
      <div className="bg-white">
        <CheckboxTable
          header={CustomerRefundsHeader}
          data={mapOrderProducts(order)}
          selectedRows={selectedRows}
          currency={order.currency}
          onCheckboxChange={handleCheckboxChange}
        />
      </div>
      {shippingData && (
        <>
          <b className="inline-block pt-6 pb-2">Refundable Shipping Fee</b>
          <div className="bg-white">
            <CheckboxTable
              header={ShippingRefundsHeader}
              data={[shippingData]}
              selectedRows={isShippingRefundSelected ? [0] : []}
              onCheckboxChange={() => {
                setIsShippingRefundSelected(!isShippingRefundSelected);
              }}
              disabledRows={
                shippingData['Available for Refund'] &&
                parseCurrency(shippingData['Available for Refund']) === 0
                  ? [0]
                  : []
              }
              columnWidths={[
                'w-[1050px]',
                'w-[280px]',
                'w-[180px]',
                'w-[200px]',
                'w-[250px]',
                'w-[280px]',
              ]}
              columnAlignments={['text-left']}
            />
          </div>
        </>
      )}
      <div className="flex items-center justify-center pt-12">
        <Button
          label="Process Refund"
          onClick={() => {
            setShowModal(true);
          }}
          isDisabled={selectedRows.length === 0 && !isShippingRefundSelected}
          variant={selectedRows.length > 0 || isShippingRefundSelected ? 'primary' : 'secondary'}
        />
        <br />
      </div>
      <div className="text-center pt-2 pb-4">
        Total Refund Amount:{' '}
        <b>
          {totalRefund} {order.currency}
        </b>
      </div>
      {showModal && (
        <ConfirmationModal
          isProcessLoading={isProcessLoading}
          isConfirmDisabled={!refundReason}
          width="w-[490px]"
          title={
            <div className="self-stretch text-center justify-start text-2xl font-bold">
              {' '}
              Please provide a refund reason to proceed with this refund of ${totalRefund}{' '}
            </div>
          }
          message={
            <div className="text-left mb-4">
              <SearchableDropdown
                options={refundReasonOptions}
                value={refundReason}
                onChange={(newSelection: any) => setRefundReason(newSelection)}
                placeholder="Select refund reason"
              />
            </div>
          }
          customMessage={
            <div className="text-center mb-4">
              {' '}
              The customer will receive an automated refund confirmation email at {
                customer.email
              }{' '}
              in approximately 20 minutes.{' '}
            </div>
          }
          confirmLabel="Confirm"
          onConfirm={handleProcessRefund}
          onCancel={() => {
            setShowModal(false);
            setRefundReason(null);
          }}
        />
      )}
    </div>
  );
};
